import Box from "@mui/material/Box";
import Link from "@mui/material/Link";
import DiscordIcon from "../../assets/svg/DiscordIcon";
import { useTranslationApp } from "../../lib/i18next";
import {
  ALERT_REBRAND_BANNER,
  ALERT_REBRAND_BANNER_APPSUMO_REVIEW_BODY,
  ALERT_REBRAND_BANNER_AFFILIATE_BODY,
  ALERT_REBRAND_BANNER_AFFILIATE_LINK,
  ALERT_REBRAND_BANNER_CAROUSEL_NEXT,
  ALERT_REBRAND_BANNER_CAROUSEL_PREVIOUS,
  ALERT_REBRAND_BANNER_DISCORD_BODY,
  DISCORD_LINK,
} from "../../i18n/keysTranslations";
import {
  LINK_ADLE_AFFILIATE,
  LINK_ADLE_DISCORD,
  LINK_APPSUMO_ADLE_REVIEWS_NEW,
  REBRAND_BANNER_APP_SUMO_CAROUSEL_INTERVAL_MS,
  REBRAND_BANNER_HEIGHT,
} from "../../utils/constants";
import useAgencyIsAppSumo from "../../hooks/useAgencyIsAppSumo";
import { useSelectorApp } from "../../lib/redux";
import { useRebrandBanner } from "../../hooks/useRebrandBanner";
import { useMemo } from "react";
import BannerMessageCarousel from "./BannerMessageCarousel";
import { Trans } from "react-i18next";
import { useLocation } from "react-router-dom";
import { isFormPath } from "../../router/formRoutes";
import { isAdminDomain } from "../../actions/auth";

const ctaBannerLinkSx = {
  display: "inline-flex",
  alignItems: "center",
  flexShrink: 0,
  fontWeight: 700,
  color: "inherit",
  "&:hover": {
    color: "inherit",
  },
};

const rebrandBannerEmojiMarginSx = {
  display: "inline-block",
  verticalAlign: "middle",
  position: "relative",
  top: "-0.05em",
  pb: 0.1,
};

const rebrandBannerMarginTransComponent = (
  <Box component="span" sx={rebrandBannerEmojiMarginSx} />
);

const DiscordBannerRow = ({ t }) => (
  <>
    <Box component="span" sx={{ flex: "0 1 auto" }}>
      {t(ALERT_REBRAND_BANNER_DISCORD_BODY)}
    </Box>
    <Link
      href={LINK_ADLE_DISCORD}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={t(DISCORD_LINK)}
      underline="always"
      sx={ctaBannerLinkSx}
    >
      <DiscordIcon width="14px" height="14px" />
      {t(DISCORD_LINK)}
    </Link>
  </>
);

const AffiliateBannerRow = ({ t }) => (
  <>
    <Box component="span" sx={{ flex: "0 1 auto" }}>
      <Trans
        i18nKey={ALERT_REBRAND_BANNER_AFFILIATE_BODY}
        components={{
          margin: rebrandBannerMarginTransComponent,
        }}
      />
    </Box>
    <Link
      href={LINK_ADLE_AFFILIATE}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={t(ALERT_REBRAND_BANNER_AFFILIATE_LINK)}
      underline="always"
      sx={ctaBannerLinkSx}
    >
      {t(ALERT_REBRAND_BANNER_AFFILIATE_LINK)}
    </Link>
  </>
);

const AppSumoReviewBannerRow = () => (
  <Box component="span" sx={{ flex: "0 1 auto" }}>
    <Trans
      i18nKey={ALERT_REBRAND_BANNER_APPSUMO_REVIEW_BODY}
      components={{
        underline: (
          <Box
            component="a"
            href={LINK_APPSUMO_ADLE_REVIEWS_NEW}
            rel="noopener noreferrer"
            target="_blank"
            sx={{
              color: "inherit",
              textDecoration: "underline",
              textUnderlineOffset: "2px",
              "&:visited": { color: "inherit" },
            }}
          />
        ),
        margin: rebrandBannerMarginTransComponent,
      }}
    />
  </Box>
);

const AFFILIATE_REBRAND_CAROUSEL_SLIDE_KEY = "rebrand-banner-affiliate";

const APP_SUMO_REBRAND_CAROUSEL_SLIDE_KEYS = [
  AFFILIATE_REBRAND_CAROUSEL_SLIDE_KEY,
  "rebrand-banner-discord",
  "rebrand-banner-appsumo-review",
];

const NON_APP_SUMO_REBRAND_CAROUSEL_SLIDE_KEYS = [
  AFFILIATE_REBRAND_CAROUSEL_SLIDE_KEY,
  "rebrand-banner-discord",
];

const RebrandBanner = ({
  appSumoCarouselSlideEntrySideForNext,
  appSumoCarouselDefaultSlideEntrySideForNext = "right",
} = {}) => {
  const { t } = useTranslationApp();
  const { showBanner } = useRebrandBanner();
  const location = useLocation();
  const { user } = useSelectorApp((state) => state.auth);
  const { isPlanActive } = useSelectorApp((state) => state.payments);
  const { isAppSumo, isAppSumoResolved } = useAgencyIsAppSumo();

  const isLoggedPerson = Boolean(user?.uid && !user?.isAnonymous);
  const hasActivePlan = isPlanActive === true;
  const showAppSumoReviewBanner =
    isLoggedPerson && hasActivePlan && isAppSumoResolved && isAppSumo;
  const showDiscordBanner =
    isLoggedPerson && hasActivePlan && isAppSumoResolved && !isAppSumo;
  const shouldPinRebrandMessage = isFormPath(location.pathname);

  const appSumoCarouselSlides = useMemo(
    () => [
      <AffiliateBannerRow
        key={APP_SUMO_REBRAND_CAROUSEL_SLIDE_KEYS[0]}
        t={t}
      />,
      <DiscordBannerRow key={APP_SUMO_REBRAND_CAROUSEL_SLIDE_KEYS[1]} t={t} />,
      <AppSumoReviewBannerRow
        key={APP_SUMO_REBRAND_CAROUSEL_SLIDE_KEYS[2]}
        t={t}
      />,
    ],
    [t]
  );

  const nonAppSumoCarouselSlides = useMemo(
    () => [
      <AffiliateBannerRow
        key={NON_APP_SUMO_REBRAND_CAROUSEL_SLIDE_KEYS[0]}
        t={t}
      />,
      <DiscordBannerRow
        key={NON_APP_SUMO_REBRAND_CAROUSEL_SLIDE_KEYS[1]}
        t={t}
      />,
    ],
    [t]
  );

  if (!showBanner || (user?.uid && !isAppSumoResolved) || isAdminDomain())
    return null;

  return (
    <Box
      sx={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        zIndex: 1202,
        minHeight: `${REBRAND_BANNER_HEIGHT}px`,
        px: 2,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexWrap: "wrap",
        rowGap: 0.5,
        columnGap: 0.5,
        backgroundColor: "primary.main",
        color: "primary.contrastText",
        textAlign: "center",
        fontSize: {
          xs: "11px",
          md: "12px",
        },
        fontWeight: 500,
        lineHeight: 1.2,
      }}
    >
      {shouldPinRebrandMessage ? (
        <Box component="span" sx={{ flex: "0 1 auto" }}>
          {t(ALERT_REBRAND_BANNER)}
        </Box>
      ) : showAppSumoReviewBanner ? (
        <BannerMessageCarousel
          slides={appSumoCarouselSlides}
          slideKeys={APP_SUMO_REBRAND_CAROUSEL_SLIDE_KEYS}
          slideEntrySideForNext={appSumoCarouselSlideEntrySideForNext}
          defaultSlideEntrySideForNext={
            appSumoCarouselDefaultSlideEntrySideForNext
          }
          autoAdvanceMs={REBRAND_BANNER_APP_SUMO_CAROUSEL_INTERVAL_MS}
          enabled={showAppSumoReviewBanner}
          ariaLabelNext={t(ALERT_REBRAND_BANNER_CAROUSEL_NEXT)}
          ariaLabelPrevious={t(ALERT_REBRAND_BANNER_CAROUSEL_PREVIOUS)}
        />
      ) : showDiscordBanner ? (
        <BannerMessageCarousel
          slides={nonAppSumoCarouselSlides}
          slideKeys={NON_APP_SUMO_REBRAND_CAROUSEL_SLIDE_KEYS}
          autoAdvanceMs={REBRAND_BANNER_APP_SUMO_CAROUSEL_INTERVAL_MS}
          enabled={showDiscordBanner}
          ariaLabelNext={t(ALERT_REBRAND_BANNER_CAROUSEL_NEXT)}
          ariaLabelPrevious={t(ALERT_REBRAND_BANNER_CAROUSEL_PREVIOUS)}
        />
      ) : (
        <Box component="span" sx={{ flex: "0 1 auto" }}>
          {t(ALERT_REBRAND_BANNER)}
        </Box>
      )}
    </Box>
  );
};

export default RebrandBanner;
